function preserveGiftWrappingSelection(current, fresh) {
  const selector = 'input[type="radio"][name="attributes[Gift wrapping]"]';
  const controls = [...current.querySelectorAll(selector)];
  if (!controls.some(input => input.checked !== input.defaultChecked)) return;
  const selected = controls.find(input => input.checked)?.value;
  if (!selected) return;
  // Digital-only and disabled configurations have no radios: keep their No value.
  fresh.querySelectorAll(selector).forEach(input => { input.checked = input.value === selected; });
}

(() => {
  if (window.NavelisCart) return;

  let returnFocus = null;
  let requestInFlight = false;
  let restoreRequest = null;

  const rootUrl = () => window.Shopify?.routes?.root || '/';
  const drawer = () => document.querySelector('[data-cart-drawer]');
  const sectionId = () => drawer()?.dataset.sectionId || 'cart-drawer';

  const updateCartCount = (itemCount) => {
    document.querySelectorAll('.site-header__cart').forEach((cartLink) => {
      let count = cartLink.querySelector('.site-header__cart-count');

      if (!count && itemCount > 0) {
        count = document.createElement('span');
        count.className = 'site-header__cart-count';
        cartLink.append(count);
      }

      if (count) {
        count.textContent = itemCount;
        count.hidden = itemCount < 1;
      }
    });
  };

  const announce = (message) => {
    const status = drawer()?.querySelector('[data-cart-drawer-status]');
    if (status) status.textContent = message;
  };

  const setBusy = (busy) => {
    requestInFlight = busy;
    const cartDrawer = drawer();
    if (!cartDrawer) return;
    if (busy) cartDrawer.setAttribute('aria-busy', 'true');
    else cartDrawer.removeAttribute('aria-busy');
  };

  const errorFromResponse = async (response, fallback) => {
    try {
      const payload = await response.json();
      return payload.description || payload.message || fallback;
    } catch (_error) {
      return fallback;
    }
  };

  const loadComplementaryProducts = async (cartDrawer) => {
    const container = cartDrawer?.querySelector('[data-cart-complementary]');
    if (!container?.dataset.url || container.dataset.loaded === 'true') return;

    container.dataset.loaded = 'true';

    try {
      const response = await fetch(container.dataset.url, { headers: { Accept: 'text/html' } });
      if (!response.ok) return;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-cart-complementary-content]');
      if (content) container.replaceChildren(content);
    } catch (_error) {
      container.replaceChildren();
    }
  };

  const giftPopup = () => document.querySelector('#aca-modal-container[open]');

  const open = (trigger = document.activeElement) => {
    if (giftPopup()) {
      close();
      return false;
    }
    const cartDrawer = drawer();
    if (!cartDrawer) return false;

    if (trigger instanceof HTMLElement) returnFocus = trigger;
    if (!cartDrawer.open) cartDrawer.showModal();
    document.body.classList.add('cart-drawer-open');
    loadComplementaryProducts(cartDrawer);
    cartDrawer.querySelector('[data-cart-drawer-close]')?.focus();
    return true;
  };

  const close = () => {
    const cartDrawer = drawer();
    if (cartDrawer?.open) cartDrawer.close();
  };

  const replaceDrawer = (sectionHtml, shouldOpen = true, focusSelector = '') => {
    if (!sectionHtml) throw new Error(drawer()?.dataset.errorMessage || 'Cart could not be updated.');

    const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const freshWrapper = html.querySelector(`#shopify-section-${CSS.escape(sectionId())}`);
    const currentWrapper = document.getElementById(`shopify-section-${sectionId()}`);

    if (!freshWrapper || !currentWrapper) throw new Error(drawer()?.dataset.errorMessage || 'Cart could not be updated.');

    if (shouldOpen) preserveGiftWrappingSelection(currentWrapper, freshWrapper);
    currentWrapper.replaceWith(freshWrapper);
    const freshDrawer = drawer();
    const itemCount = Number(freshDrawer?.dataset.cartCount || 0);
    updateCartCount(itemCount);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount } }));

    if (shouldOpen) {
      open(returnFocus);
      if (focusSelector) freshDrawer?.querySelector(focusSelector)?.focus();
    }
    return freshDrawer;
  };

  const request = async (path, payload, { shouldOpen = true, focusSelector = '' } = {}) => {
    if (restoreRequest) await restoreRequest;
    if (requestInFlight) return null;

    const fallback = drawer()?.dataset.errorMessage || 'Cart could not be updated.';
    const requestedSection = sectionId();
    let addSucceeded = false;
    setBusy(true);
    announce('');

    try {
      const response = await fetch(`${rootUrl()}${path}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          ...payload,
          sections: [requestedSection],
          sections_url: `${window.location.pathname}${window.location.search}`,
        }),
      });

      if (!response.ok) throw new Error(await errorFromResponse(response, fallback));
      addSucceeded = path === 'cart/add.js';
      const result = await response.json();
      replaceDrawer(result.sections?.[requestedSection], shouldOpen, focusSelector);
      return result;
    } catch (error) {
      setBusy(false);
      const message = addSucceeded
        ? drawer()?.dataset.addRefreshError || 'Your item was added. Open your cart before trying again.'
        : error.message || fallback;
      announce(message);
      throw new Error(message);
    } finally {
      requestInFlight = false;
    }
  };

  const add = async (formData, trigger) => {
    const items = [];
    const properties = {};
    const item = {
      id: Number(formData.get('id')),
      quantity: Number(formData.get('quantity') || 1),
    };

    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = value;
    });

    if (Object.keys(properties).length) item.properties = properties;
    if (formData.get('selling_plan')) item.selling_plan = Number(formData.get('selling_plan'));
    items.push(item);
    returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
    return request('cart/add.js', { items }, { shouldOpen: true });
  };

  const changeLine = (key, quantity, focusSelector = '') => request('cart/change.js', { id: key, quantity }, { shouldOpen: true, focusSelector });
  const updateCart = (payload, focusSelector = '') => request('cart/update.js', payload, { shouldOpen: true, focusSelector });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cart-drawer-trigger]');
    if (trigger && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && drawer()) {
      event.preventDefault();
      open(trigger);
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      close();
      return;
    }

    const cartDrawer = event.target.closest('[data-cart-drawer]');
    if (!cartDrawer) return;

    if (event.target === cartDrawer) {
      close();
      return;
    }

    const quantityButton = event.target.closest('[data-quantity-change]');
    if (quantityButton) {
      const line = quantityButton.closest('[data-cart-line]');
      const input = line?.querySelector('[data-quantity-input]');
      if (!line || !input) return;

      if (quantityButton.dataset.quantityChange === 'increase') input.stepUp();
      else input.stepDown();

      const focusSelector = `[data-line-key="${CSS.escape(line.dataset.lineKey)}"] [data-quantity-change="${quantityButton.dataset.quantityChange}"]`;
      changeLine(line.dataset.lineKey, Number(input.value), focusSelector).catch(() => {});
      return;
    }

    const removeLink = event.target.closest('[data-cart-remove]');
    if (removeLink) {
      const line = removeLink.closest('[data-cart-line]');
      if (!line) return;
      event.preventDefault();
      changeLine(line.dataset.lineKey, 0).catch(() => {});
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-drawer] [data-quantity-input]');
    if (!input) return;
    const line = input.closest('[data-cart-line]');
    if (!line) return;
    const focusSelector = `[data-line-key="${CSS.escape(line.dataset.lineKey)}"] [data-quantity-input]`;
    changeLine(line.dataset.lineKey, Math.max(0, Number(input.value) || 0), focusSelector).catch(() => {});
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !drawer()?.open) return;
    event.preventDefault();
    close();
  });

  document.addEventListener('submit', (event) => {
    const itemsForm = event.target.closest('[data-cart-drawer-items]');
    if (itemsForm) {
      event.preventDefault();
      const updates = {};
      new FormData(itemsForm).forEach((value, key) => {
        const match = key.match(/^updates\[(.+)\]$/);
        if (match) updates[match[1]] = Number(value);
      });
      updateCart({ updates }).catch(() => {});
      return;
    }

    const discountForm = event.target.closest('[data-cart-discount-form]');
    if (discountForm) {
      event.preventDefault();
      const discount = String(new FormData(discountForm).get('discount') || '').trim();
      updateCart({ discount }, '[data-cart-discount-summary]').catch(() => {});
      return;
    }

    const noteForm = event.target.closest('[data-cart-note-form]');
    if (noteForm) {
      event.preventDefault();
      const note = String(new FormData(noteForm).get('note') || '');
      updateCart({ note }, '[data-cart-note-summary]').catch(() => {});
    }
  });

  document.addEventListener('close', (event) => {
    if (!event.target.matches('[data-cart-drawer]')) return;
    document.body.classList.remove('cart-drawer-open');
    if (!giftPopup() && returnFocus?.isConnected) returnFocus.focus();
    returnFocus = null;
  }, true);

  document.addEventListener('shopify:section:load', (event) => {
    const cartDrawer = event.target.querySelector('[data-cart-drawer]');
    if (cartDrawer) loadComplementaryProducts(cartDrawer);
  });

  window.addEventListener('pageshow', (event) => {
    const navigation = window.performance?.getEntriesByType('navigation')[0];
    if ((!event.persisted && navigation?.type !== 'back_forward') || restoreRequest || requestInFlight || !drawer()) return;

    // History can restore HTML from before the last cart mutation or checkout.
    restoreRequest = (async () => {
      try {
        const response = await fetch(`${rootUrl()}cart?section_id=${encodeURIComponent(sectionId())}`, {
          headers: { Accept: 'text/html' }, cache: 'no-store',
        });
        if (!response.ok) throw new Error(drawer()?.dataset.errorMessage);
        const sectionHtml = await response.text();
        replaceDrawer(sectionHtml, Boolean(drawer()?.open));
      } catch (_error) {
        // A fresh full cart remains available when the section cannot be restored.
        document.querySelectorAll('[data-cart-drawer-trigger]').forEach((trigger) => {
          trigger.removeAttribute('data-cart-drawer-trigger');
        });
        close();
      } finally {
        restoreRequest = null;
      }
    })();
  });

  const refresh = async (cart) => {
    if (cart && Number.isFinite(cart.item_count)) updateCartCount(cart.item_count);
    if (!drawer()) return;
    if (restoreRequest) await restoreRequest;
    const fallback = () => {
      document.querySelectorAll('[data-cart-drawer-trigger]').forEach(trigger => trigger.removeAttribute('data-cart-drawer-trigger'));
      close();
    };
    if (requestInFlight) { fallback(); return; }
    // Existing drawer mutations wait for restoreRequest before posting. This prevents
    // a stale section response from replacing the result of a later drawer mutation.
    restoreRequest = (async () => {
      try {
        const response = await fetch(rootUrl() + 'cart?section_id=' + encodeURIComponent(sectionId()), { headers: { Accept: 'text/html' }, cache: 'no-store' });
        if (!response.ok) throw new Error('cart_unavailable');
        replaceDrawer(await response.text(), Boolean(drawer()?.open));
      } catch (_) { fallback(); }
    })();
    try { await restoreRequest; } finally { restoreRequest = null; }
  };

  let giftWasOpen = false;
  let giftRefreshTimer = null;
  const refreshAfterGift = () => {
    if (requestInFlight || restoreRequest) {
      giftRefreshTimer = window.setTimeout(refreshAfterGift, 100);
      return;
    }
    giftRefreshTimer = null;
    refresh().catch(() => {});
  };
  const syncGiftPopup = () => {
    const popup = giftPopup();
    if (popup) {
      const cartDrawer = drawer();
      if (cartDrawer?.open) {
        close();
        popup.querySelector('button:not([disabled])')?.focus();
      }
      if (giftRefreshTimer) window.clearTimeout(giftRefreshTimer);
      giftRefreshTimer = null;
    } else if (giftWasOpen) {
      giftRefreshTimer = window.setTimeout(refreshAfterGift, 100);
    }
    giftWasOpen = Boolean(popup);
  };
  const giftObserver = new MutationObserver(syncGiftPopup);
  giftObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
  syncGiftPopup();

  window.NavelisCart = { add, close, open, updateCart, refresh };
})();



(() => {
  const configNode = document.getElementById('gj-gift-config');
  if (!configNode || window.GriffeCheckoutGifts) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
  const en = document.documentElement.lang.startsWith('en');
  const words = en ? {
    title: 'Choose your gifts', intro: 'Your Griffe Joyaux essentials, on us. Choose your gifts before checkout.',
    first: 'Your complimentary accessory', extra: 'Your additional accessories', apron: 'Your signature apron',
    choose: 'Choose', remove: 'Remove', selected: 'selected', free: 'Complimentary',
    confirm: 'Confirm and checkout', skip: 'Continue without more gifts', back: 'Back to my cart',
    error: 'Your gifts could not be confirmed. Try again, or continue without more gifts.',
    busy: 'Confirming your gifts…', limit: 'Choose up to', unit: 'gift(s)'
  } : {
    title: 'Choisis tes cadeaux', intro: 'Tes essentiels Griffe Joyaux, offerts. Fais ton choix avant de passer au paiement.',
    first: 'Ton accessoire offert', extra: 'Tes accessoires supplémentaires', apron: 'Ton tablier signature',
    choose: 'Choisir', remove: 'Retirer', selected: 'sélectionné(s)', free: 'Offert',
    confirm: 'Valider et passer au paiement', skip: 'Continuer sans autre cadeau', back: 'Revenir à mon panier',
    error: 'Tes cadeaux n’ont pas pu être confirmés. Réessaie ou continue sans autre cadeau.',
    busy: 'Confirmation de tes cadeaux…', limit: 'Choisis jusqu’à', unit: 'cadeau(x)'
  };
  const rules = [
    { name: 'GIFT LIME', threshold: 100, max: 1, title: words.first, products: [0, 1, 2] },
    { name: 'GIFT LIME 2', threshold: 7500, max: 2, title: words.extra, products: [0, 1, 2] },
    { name: 'GIFT TABLIER', threshold: 20000, max: 1, title: words.apron, products: [3] }
  ];
  const root = () => window.Shopify?.routes?.root || '/';
  const giftRule = item => rules.find(rule => item.properties?._Gifted === rule.name);
  const subtotal = cart => cart.items.reduce((sum, item) => sum + (giftRule(item) ? 0 : item.final_line_price), 0);
  const qualifies = (rule, cart) => subtotal(cart) >= Math.round(rule.threshold * Number(window.Shopify?.currency?.rate || 1));
  const gifted = (rule, cart) => cart.items.filter(item => item.properties?._Gifted === rule.name).reduce((sum, item) => sum + item.quantity, 0);
  const request = async (path, payload) => {
    const response = await fetch(root() + path, payload ? {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload)
    } : { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'cart_unavailable');
    return result;
  };
  const clean = async () => {
    let cart = await request('cart.js');
    const updates = {};
    for (const rule of rules) {
      let allowance = qualifies(rule, cart) ? rule.max : 0;
      for (const item of cart.items.filter(item => item.properties?._Gifted === rule.name)) {
        const quantity = item.final_line_price > 0 ? 0 : Math.min(item.quantity, allowance);
        allowance -= quantity;
        if (quantity !== item.quantity) updates[item.key] = quantity;
      }
    }
    if (Object.keys(updates).length) cart = await request('cart/update.js', { updates });
    return { cart, changed: Object.keys(updates).length > 0 };
  };
  let cleaning = null;
  const cleanup = () => cleaning ||= clean().finally(() => { cleaning = null; });
  let current = null;
  let dialog = null;
  let handling = false;
  let bypass = false;
  let returnTo = null;
  const selections = new Map();
  const reviewed = cart => {
    try { return JSON.parse(sessionStorage.getItem('gj-gifts-' + cart.token) || '[]'); } catch (_) { return []; }
  };
  const markReviewed = cart => {
    try { sessionStorage.setItem('gj-gifts-' + cart.token, JSON.stringify(rules.filter(rule => qualifies(rule, cart)).map(rule => rule.name))); } catch (_) {}
  };
  const missing = cart => rules.filter(rule => qualifies(rule, cart) && gifted(rule, cart) < rule.max && !reviewed(cart).includes(rule.name));
  const variant = index => config.products[index]?.variants?.find(v => v.title === '1' || v.title === 'Default Title');
  const available = rule => rule.products.filter(index => variant(index)?.available);
  const node = (tag, text, className) => {
    const element = document.createElement(tag);
    if (text) element.textContent = text;
    if (className) element.className = className;
    return element;
  };
  const button = (text, action, className) => {
    const element = node('button', text, className);
    element.type = 'button';
    element.addEventListener('click', action);
    return element;
  };
  const style = node('style');
  style.textContent = `
    .gj-gifts{width:min(760px,calc(100vw - 24px));max-height:calc(100dvh - 24px);margin:auto;padding:0;border:1px solid #696762;border-radius:0;background:#0a0a0a;color:#f1eee8;overflow:auto;font:400 14px/1.6 "Work Sans",sans-serif;box-sizing:border-box}
    .gj-gifts[open]{display:flex;flex-direction:column}.gj-gifts::backdrop{background:rgba(0,0,0,.8)}
    .gj-gifts__body{padding:28px 34px;overflow-y:auto;min-height:0}
    .gj-gifts h2{margin:8px 0 14px;font:400 clamp(30px,5vw,46px)/1.05 "Bodoni FLF",serif;text-transform:uppercase;color:#f1eee8}
    .gj-gifts__intro{color:#c7c2b9;margin:0 0 26px}.gj-gifts__eyebrow{font-size:10px;letter-spacing:.16em;text-transform:uppercase}
    .gj-gifts fieldset{padding:20px 0;margin:0;border:0;border-top:1px solid #393835;min-width:0}.gj-gifts legend{padding:0 10px 0 0;font-weight:500}.gj-gifts__count{font-size:12px;color:#c7c2b9;margin:0 0 12px}
    .gj-gifts__grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px}.gj-gifts__card{text-align:center;min-width:0}.gj-gifts__card img{width:100%;height:140px;object-fit:contain;background:#f1eee8}.gj-gifts__name{font-size:11px;line-height:1.5;margin:10px 0;text-transform:uppercase}.gj-gifts__controls{display:flex;gap:6px;justify-content:center;align-items:center}
    .gj-gifts button{min-height:44px;border:1px solid #696762;background:transparent;color:#f1eee8;border-radius:0;font:500 11px/1.4 "Work Sans",sans-serif;letter-spacing:.05em;padding:10px;cursor:pointer}.gj-gifts button:disabled{opacity:.45;cursor:default}.gj-gifts button:focus-visible{outline:2px solid #f1eee8;outline-offset:3px}.gj-gifts button[aria-pressed=true]{background:#f1eee8;color:#0a0a0a}
    .gj-gifts__footer{flex-shrink:0;display:grid;gap:10px;background:#0a0a0a;border-top:1px solid #393835;padding:20px 34px}.gj-gifts .gj-gifts__confirm{background:#f1eee8;color:#0a0a0a;border-color:#f1eee8;text-transform:uppercase}.gj-gifts__status{margin:0;color:#c7c2b9;font-size:12px}.gj-gifts .gj-gifts__back{border:0;padding:0;min-height:32px;text-decoration:underline}.gj-gifts-active .cart-page__accelerated-checkout{display:none}

    .gj-customs{width:min(640px,calc(100vw - 24px))}.gj-customs form{display:flex;flex-direction:column;min-height:0;width:100%}.gj-customs .gj-customs__choices{display:grid;gap:14px;padding:0 0 20px;border:0}.gj-customs__choice{display:flex;align-items:flex-start;gap:12px;cursor:pointer}.gj-customs__choice input{width:18px;height:18px;margin:3px 0 0;flex-shrink:0;accent-color:#f1eee8}.gj-customs__fields[hidden]{display:none}.gj-customs__field{display:grid;gap:6px;margin:18px 0 0}.gj-customs__field label{font-size:13px}.gj-customs__field input,.gj-customs__field select{box-sizing:border-box;width:100%;min-height:46px;padding:10px 12px;border:1px solid #696762;border-radius:0;background:#141414;color:#f1eee8;font:400 16px/1.4 "Work Sans",sans-serif}.gj-customs__field input:focus-visible,.gj-customs__field select:focus-visible,.gj-customs__choice input:focus-visible{outline:2px solid #f1eee8;outline-offset:3px}.gj-customs__help,.gj-customs__note{color:#c7c2b9;font-size:12px;margin:10px 0}.gj-customs a{color:#f1eee8;font-size:12px;text-decoration:underline}.gj-customs .gj-gifts__status{min-height:0}.gj-customs .gj-gifts__status:empty{display:none}
    @media(max-width:540px){.gj-gifts__body{padding:26px 18px}.gj-gifts__card img{height:auto;aspect-ratio:1}.gj-gifts__footer{padding:16px 18px}.gj-gifts__grid{gap:8px}.gj-gifts__name{font-size:10px}.gj-gifts button{font-size:10px;padding:8px 6px}.gj-gifts__controls{flex-wrap:wrap}.gj-gifts h2{font-size:34px}}
  `;
  document.head.append(style);
  document.documentElement.classList.add('gj-gifts-active');
  const resume = () => {
    dialog?.close();
    bypass = true;
    current?.resume();
    window.setTimeout(() => { bypass = false; handling = false; }, 1000);
  };
  const setBusy = busy => {
    dialog.setAttribute('aria-busy', String(busy));
    dialog.querySelectorAll('button').forEach(element => { element.disabled = busy; });
  };
  const render = cart => {
    dialog?.remove();
    selections.clear();
    dialog = node('dialog', '', 'gj-gifts');
    dialog.setAttribute('aria-labelledby', 'gj-gifts-title');
    const body = node('div', '', 'gj-gifts__body');
    body.append(node('p', 'Griffe Joyaux', 'gj-gifts__eyebrow'));
    const title = node('h2', words.title); title.id = 'gj-gifts-title';
    body.append(title, node('p', words.intro, 'gj-gifts__intro'));
    const groups = missing(cart).filter(rule => available(rule).length);
    for (const rule of groups) {
      const capacity = rule.max - gifted(rule, cart);
      const choices = new Map(); selections.set(rule.name, choices);
      const field = node('fieldset'); field.append(node('legend', rule.title));
      const count = node('p', en ? `Choose up to ${capacity} ${capacity === 1 ? 'gift' : 'gifts'}` : `Choisis jusqu’à ${capacity} ${capacity === 1 ? 'cadeau' : 'cadeaux'}`, 'gj-gifts__count'); field.append(count);
      const grid = node('div', '', 'gj-gifts__grid');
      const controls = [];
      for (const index of available(rule)) {
        const product = config.products[index];
        const card = node('div', '', 'gj-gifts__card');
        const image = node('img'); image.src = product.featured_image; image.alt = product.title; image.width = 200; image.height = 200;
        card.append(image, node('p', product.title, 'gj-gifts__name'));
        const row = node('div', '', 'gj-gifts__controls');
        const total = () => Array.from(choices.values()).reduce((sum, value) => sum + value, 0);
        const refresh = () => {
          count.textContent = en ? `${total()} of ${capacity} selected` : `${total()} sur ${capacity} ${total() > 1 ? 'sélectionnés' : 'sélectionné'}`;
          controls.forEach(control => {
            const quantity = choices.get(control.index) || 0;
            control.add.textContent = quantity ? `${words.choose} (${quantity})` : words.choose;
            control.add.setAttribute('aria-pressed', String(quantity > 0));
            control.add.disabled = total() >= capacity;
            control.remove.hidden = !quantity;
          });
        };
        const add = button(words.choose, () => { if (total() < capacity) choices.set(index, (choices.get(index) || 0) + 1); refresh(); });
        add.setAttribute('aria-label', `${words.choose} ${product.title}`); add.setAttribute('aria-pressed', 'false');
        const remove = button('−', () => { choices.set(index, Math.max(0, (choices.get(index) || 0) - 1)); refresh(); });
        remove.setAttribute('aria-label', `${words.remove} ${product.title}`); remove.hidden = true;
        row.append(add, remove); card.append(row); grid.append(card); controls.push({index, add, remove});
      }
      field.append(grid); body.append(field);
    }
    const footer = node('div', '', 'gj-gifts__footer');
    const status = node('p', '', 'gj-gifts__status'); status.setAttribute('role', 'status');
    const confirm = button(words.confirm, async () => {
      setBusy(true); status.textContent = words.busy;
      try {
        const latest = (await cleanup()).cart;
        const items = [];
        for (const rule of rules) {
          let allowance = qualifies(rule, latest) ? Math.max(0, rule.max - gifted(rule, latest)) : 0;
          for (const [index, quantity] of selections.get(rule.name) || []) {
            const amount = Math.min(quantity, allowance);
            if (amount) items.push({ id: variant(index).id, quantity: amount, properties: { _Gifted: rule.name } });
            allowance -= amount;
          }
        }
        if (items.length) await request('cart/add.js', {items});
        const after = (await cleanup()).cart;
        const charged = after.items.filter(item => giftRule(item) && item.final_line_price > 0);
        if (items.some(added => !after.items.some(item => item.variant_id === added.id && item.properties?._Gifted === added.properties._Gifted && item.final_line_price === 0))) throw new Error('gift_not_confirmed');
        if (charged.length) {
          await request('cart/update.js', {updates: Object.fromEntries(charged.map(item => [item.key, 0]))});
          throw new Error('gift_not_free');
        }
        markReviewed(after);
        resume();
      } catch (_) {
        status.textContent = words.error; setBusy(false);
      }
    }, 'gj-gifts__confirm');
    const skip = button(words.skip, async () => {
      setBusy(true);
      try { const after = (await cleanup()).cart; markReviewed(after); resume(); }
      catch (_) { status.textContent = words.error; setBusy(false); }
    });
    const back = button(words.back, () => dialog.close(), 'gj-gifts__back');
    footer.append(status, confirm, skip, back); dialog.append(body, footer); document.body.append(dialog);
    dialog.addEventListener('cancel', event => { if (dialog.getAttribute('aria-busy') === 'true') event.preventDefault(); });
    dialog.addEventListener('close', () => { if (!bypass) { handling = false; returnTo?.focus(); } });
    window.NavelisCart?.close(); dialog.showModal(); confirm.focus();
  };
  const collectCustoms = () => new Promise(resolve => {
    const text = en ? {
      title: 'Delivery to Overseas France',
      intro: 'Are you ordering for a business receiving this parcel in Guadeloupe, Martinique, French Guiana or Réunion?',
      yes: 'Yes, for a business in Overseas France',
      no: 'No, this does not apply to my order',
      note: 'For these business shipments, Colissimo needs the recipient’s EORI for customs clearance.',
      territory: 'Delivery destination', choose: 'Choose your destination',
      company: 'Business name', eori: 'Recipient’s EORI number', vat: 'VAT number (if you have one)',
      help: 'Enter the EORI registered with French Customs, for example FR123456789. A SIREN alone is not an EORI.',
      link: 'Apply for an EORI with French Customs',
      continue: 'Continue', back: 'Back to my cart',
      saving: 'Saving your information…',
      error: 'Your information could not be saved. Please try again before continuing.',
      loadError: 'Your previous information could not be loaded. You can enter it again and retry.',
      invalid: 'Enter a French EORI: FR followed by 9 digits.',
      missing: 'Please select the option that applies to your order.'
    } : {
      title: 'Livraison en Outre-mer',
      intro: 'Commandez-vous pour une entreprise qui recevra ce colis en Guadeloupe, Martinique, Guyane ou à La Réunion ?',
      yes: 'Oui, pour une entreprise en Outre-mer',
      no: 'Non, ma commande n’est pas concernée',
      note: 'Pour ces envois professionnels, Colissimo a besoin de l’EORI du destinataire pour le dédouanement.',
      territory: 'Destination de livraison', choose: 'Choisissez votre destination',
      company: 'Nom de l’entreprise', eori: 'Numéro EORI du destinataire', vat: 'Numéro de TVA (si vous en disposez)',
      help: 'Indiquez l’EORI enregistré auprès des douanes françaises, par exemple FR123456789. Le SIREN seul ne suffit pas.',
      link: 'Demander un EORI auprès des douanes',
      continue: 'Continuer', back: 'Revenir à mon panier',
      saving: 'Enregistrement de vos informations…',
      error: 'Vos informations n’ont pas pu être enregistrées. Réessayez avant de continuer.',
      loadError: 'Vos précédentes informations n’ont pas pu être chargées. Vous pouvez les saisir à nouveau et réessayer.',
      invalid: 'Indiquez un EORI français : FR suivi de 9 chiffres.',
      missing: 'Sélectionnez la réponse correspondant à votre commande.'
    };
    const keys = { type: 'Livraison professionnelle Outre-mer', territory: 'Destination professionnelle Outre-mer', company: 'Entreprise destinataire', eori: 'EORI destinataire', vat: 'TVA destinataire' };
    const modal = node('dialog', '', 'gj-gifts gj-customs');
    modal.setAttribute('aria-labelledby', 'gj-customs-title');
    const form = node('form');
    const body = node('div', '', 'gj-gifts__body');
    const title = node('h2', text.title); title.id = 'gj-customs-title';
    body.append(node('p', 'Griffe Joyaux', 'gj-gifts__eyebrow'), title, node('p', text.intro, 'gj-gifts__intro'));
    const choices = node('fieldset', '', 'gj-customs__choices');
    const radios = ['yes', 'no'].map(value => {
      const label = node('label', '', 'gj-customs__choice');
      const input = node('input'); input.type = 'radio'; input.name = 'gj-customs-type'; input.value = value; input.required = true;
      label.append(input, node('span', text[value])); choices.append(label);
      return input;
    });
    const fields = node('div', '', 'gj-customs__fields'); fields.hidden = true;
    fields.append(node('p', text.note, 'gj-customs__note'));
    const field = (labelText, id, input) => {
      const wrapper = node('div', '', 'gj-customs__field');
      const label = node('label', labelText); label.htmlFor = id; input.id = id;
      wrapper.append(label, input); fields.append(wrapper); return input;
    };
    const territory = field(text.territory, 'gj-customs-territory', node('select'));
    const destinations = [['', text.choose], ['Guadeloupe', 'Guadeloupe'], ['Martinique', 'Martinique'], ['Guyane', en ? 'French Guiana' : 'Guyane'], ['La Réunion', en ? 'Réunion' : 'La Réunion']];
    for (const [value, label] of destinations) { const option = node('option', label); option.value = value; territory.append(option); }
    const company = field(text.company, 'gj-customs-company', node('input')); company.type = 'text'; company.maxLength = 150; company.autocomplete = 'organization';
    const eori = field(text.eori, 'gj-customs-eori', node('input')); eori.type = 'text'; eori.maxLength = 30; eori.placeholder = 'FR123456789'; eori.autocomplete = 'off'; eori.spellcheck = false;
    const help = node('p', text.help, 'gj-customs__help'); help.id = 'gj-customs-help'; eori.setAttribute('aria-describedby', help.id);
    fields.append(help);
    const link = node('a', text.link); link.href = 'https://www.douane.gouv.fr/demarche/enregistrer-votre-entreprise-aupres-de-la-douane-numero-eori'; link.target = '_blank'; link.rel = 'noopener noreferrer'; fields.append(link);
    const vat = field(text.vat, 'gj-customs-vat', node('input')); vat.type = 'text'; vat.maxLength = 30; vat.autocomplete = 'off'; vat.spellcheck = false;
    const updateFields = () => {
      const professional = radios[0].checked;
      fields.hidden = !professional;
      for (const input of [territory, company, eori, vat]) input.disabled = !professional;
      for (const input of [territory, company, eori]) input.required = professional;
      eori.setCustomValidity('');
    };
    for (const radio of radios) radio.addEventListener('change', () => { status.textContent = ''; updateFields(); });
    eori.addEventListener('input', () => eori.setCustomValidity(''));
    body.append(choices, fields);
    const footer = node('div', '', 'gj-gifts__footer');
    const status = node('p', '', 'gj-gifts__status'); status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
    const proceed = node('button', text.continue, 'gj-gifts__confirm'); proceed.type = 'submit';
    const back = button(text.back, () => modal.close(), 'gj-gifts__back');
    footer.append(status, proceed, back); form.append(body, footer); modal.append(form); document.body.append(modal);
    let busy = false;
    let accepted = false;
    const setBusy = value => {
      busy = value; modal.setAttribute('aria-busy', String(value));
      for (const input of form.querySelectorAll('input, select, button')) input.disabled = value;
      if (!value) updateFields();
    };
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (busy) return;
      if (!radios.some(radio => radio.checked)) { status.textContent = text.missing; radios[0].focus(); return; }
      const professional = radios[0].checked;
      if (professional) {
        company.value = company.value.trim();
        eori.value = eori.value.replace(/\s/g, '').toUpperCase();
        eori.setCustomValidity(/^FR\d{9}$/.test(eori.value) ? '' : text.invalid);
      }
      if (!form.reportValidity()) return;
      const attributes = {
        [keys.type]: professional ? 'Oui' : 'Non',
        [keys.territory]: professional ? territory.value : '',
        [keys.company]: professional ? company.value : '',
        [keys.eori]: professional ? eori.value : '',
        [keys.vat]: professional ? vat.value.replace(/\s/g, '').toUpperCase() : ''
      };
      setBusy(true); status.textContent = text.saving;
      try {
        const saved = await request('cart/update.js', { attributes });
        if (Object.entries(attributes).some(([key, value]) => (saved.attributes?.[key] || '') !== value)) throw new Error('customs_not_saved');
        accepted = true; modal.close();
      } catch (_) { status.textContent = text.error; setBusy(false); }
    });
    modal.addEventListener('cancel', event => { if (busy) event.preventDefault(); });
    modal.addEventListener('close', () => { modal.remove(); resolve(accepted); });
    window.NavelisCart?.close(); modal.showModal(); setBusy(true);
    request('cart.js').then(cart => {
      const attributes = cart.attributes || {};
      if (attributes[keys.type] === 'Oui') radios[0].checked = true;
      if (attributes[keys.type] === 'Non') radios[1].checked = true;
      territory.value = attributes[keys.territory] || '';
      company.value = attributes[keys.company] || '';
      eori.value = attributes[keys.eori] || '';
      vat.value = attributes[keys.vat] || '';
    }).catch(() => { status.textContent = text.loadError; }).finally(() => { setBusy(false); radios[0].focus(); });
  });

  const enter = async (element, continuation, pending) => {
    if (handling) return;
    handling = true; returnTo = element; current = {resume: continuation};
    if (!await collectCustoms()) { handling = false; returnTo?.focus(); return; }
    try {
      for (let attempt = 0; document.querySelector('[data-cart-drawer][aria-busy=true]') && attempt < 100; attempt++) {
        await new Promise(resolve => window.setTimeout(resolve, 50));
      }
      if (pending) await request('cart/update.js', pending);
      const result = await cleanup();
      config.products = await Promise.all(config.products.map(product => request('products/' + product.handle + '.js')));
      if (!missing(result.cart).some(rule => available(rule).length)) { resume(); return; }
      render(result.cart);
    } catch (_) { resume(); }
  };
  let quickStarting = false;
  document.addEventListener('submit', event => {
    if (!bypass && event.submitter?.matches('[data-buy-now]') && window.NavelisCart?.add) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (quickStarting || handling) return;
      quickStarting = true;
      const submitter = event.submitter;
      const fields = new FormData(event.target);
      submitter.disabled = true;
      window.NavelisCart.add(fields, submitter)
        .then(() => enter(submitter, () => location.assign(root() + 'checkout')))
        .catch(() => window.NavelisCart.open(submitter))
        .finally(() => { quickStarting = false; submitter.disabled = false; });
      return;
    }
    if (bypass || event.submitter?.name !== 'checkout') return;
    const form = event.target; const submitter = event.submitter;
    event.preventDefault(); event.stopImmediatePropagation();
    const fields = new FormData(form);
    const updates = {};
    for (const [name, value] of fields) {
      const match = name.match(/^updates\[(.+)\]$/);
      if (match) updates[match[1]] = Math.max(0, Math.floor(Number(value) || 0));
    }
    const pending = Object.keys(updates).length ? { updates, ...(fields.has('note') ? {note: fields.get('note')} : {}) } : null;
    enter(submitter, () => { if (pending) location.assign(root() + 'checkout'); else if (form.isConnected && submitter.isConnected) form.requestSubmit(submitter); else location.assign(root() + 'checkout'); }, pending);
  }, true);
  document.addEventListener('click', event => {
    if (bypass || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || !/\/checkout\/?$/.test(url.pathname)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    enter(link, () => location.assign(link.href));
  }, true);
  const tidy = async () => {
    if (handling || document.querySelector('[data-cart-drawer][aria-busy=true]')) return;
    try { const result = await cleanup(); if (result.changed) { if (document.querySelector('[data-cart-section]')) { location.reload(); return; } await window.NavelisCart?.refresh(result.cart); } } catch (_) {}
  };
  document.addEventListener('cart:updated', tidy);
  window.addEventListener('pageshow', tidy);
  window.GriffeCheckoutGifts = { cleanup };
  tidy();
})();
